const asNumber = (value, fallback = 0) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
}

const asBool = (value) => value === true || value === 1 || value === '1'

const buildStatusEpisodes = (events = [], createdAt = 0, now = 0) => {
    const rows = (Array.isArray(events) ? events : [])
        .map((event, index) => ({
            statusId: asNumber(event?.id_status, 0),
            enteredAt: Math.max(createdAt, asNumber(event?.time_insert, createdAt)),
            id: asNumber(event?.id, index),
        }))
        .filter((event) => event.statusId > 0)
        .sort((a, b) => a.enteredAt - b.enteredAt || a.id - b.id)
    const episodes = []
    rows.forEach((row) => {
        const previous = episodes[episodes.length - 1]
        if (!previous || previous.statusId !== row.statusId) episodes.push(row)
    })
    return episodes.map((episode, index) => ({
        ...episode,
        leftAt: episodes[index + 1]?.enteredAt || now,
        completed: index < episodes.length - 1,
    }))
}

const calculateHistoricalViolations = (order, statuses, createdAt, now) => {
    if (Array.isArray(order?.executionTiming?.historicalViolations)) {
        return order.executionTiming.historicalViolations
    }
    const statusMap = new Map((statuses || []).map((status) => [asNumber(status?.id, 0), status]))
    const consumedByStatus = {}
    const violations = new Map()
    buildStatusEpisodes(order?.statusOrderAll, createdAt, now).forEach((episode) => {
        if (!episode.completed) return
        const status = statusMap.get(episode.statusId) || {}
        const statusName = status?.name || `Статус #${episode.statusId}`
        const duration = Math.max(0, episode.leftAt - episode.enteredAt)
        const plannedMinutes = Math.max(0, asNumber(status?.planned_minutes, 0))
        const deadlineAt = plannedMinutes > 0 ? createdAt + plannedMinutes * 60 : 0
        if (deadlineAt > 0 && episode.leftAt > deadlineAt) {
            violations.set(`checkpoint_${episode.statusId}`, {
                type: episode.statusId === 3 ? 'final' : 'checkpoint',
                statusId: episode.statusId,
                statusName,
                limitMinutes: plannedMinutes,
                actualSeconds: Math.max(0, episode.leftAt - createdAt),
                overdueSeconds: Math.max(0, episode.leftAt - deadlineAt),
                violatedAt: episode.leftAt,
            })
        }
        const stageLimitMinutes = Math.max(0, asNumber(status?.stage_limit_minutes, 0))
        const consumed = Math.max(0, asNumber(consumedByStatus[episode.statusId], 0)) + duration
        consumedByStatus[episode.statusId] = consumed
        if (stageLimitMinutes > 0 && consumed > stageLimitMinutes * 60) {
            violations.set(`stage_${episode.statusId}`, {
                type: 'stage',
                statusId: episode.statusId,
                statusName,
                limitMinutes: stageLimitMinutes,
                actualSeconds: consumed,
                overdueSeconds: Math.max(0, consumed - stageLimitMinutes * 60),
                violatedAt: episode.leftAt,
            })
        }
    })
    return Array.from(violations.values())
}

const calculateAccumulatedStatusSeconds = (events = [], statusId = 0, createdAt = 0, currentEnteredAt = 0) => {
    const rows = (Array.isArray(events) ? events : [])
        .map((event, index) => ({
            statusId: asNumber(event?.id_status, 0),
            workerId: asNumber(event?.id_worker, 0),
            enteredAt: Math.max(createdAt, asNumber(event?.time_insert, createdAt)),
            id: asNumber(event?.id, index),
        }))
        .filter((event) => event.statusId > 0)
        .sort((a, b) => a.enteredAt - b.enteredAt || a.id - b.id)
    const episodes = []
    rows.forEach((row) => {
        const previous = episodes[episodes.length - 1]
        if (!previous || previous.statusId !== row.statusId) episodes.push(row)
    })
    return episodes.reduce((total, episode, index) => {
        const leftAt = episodes[index + 1]?.enteredAt || currentEnteredAt
        if (episode.statusId !== statusId || episode.enteredAt >= currentEnteredAt || leftAt > currentEnteredAt) return total
        return total + Math.max(0, leftAt - episode.enteredAt)
    }, 0)
}

export const formatExecutionDuration = (seconds = 0) => {
    const totalMinutes = Math.max(0, Math.ceil(asNumber(seconds, 0) / 60))
    if (totalMinutes < 60) return `${totalMinutes} мин`
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return minutes > 0 ? `${hours} ч ${minutes} мин` : `${hours} ч`
}

export const calculateOrderExecutionTiming = (order = {}, statuses = [], nowMs = Date.now()) => {
    const statusId = asNumber(order?.statusOrder?.id_status || order?.executionTiming?.statusId, 0)
    const status = (statuses || []).find((item) => asNumber(item?.id, 0) === statusId) || {}
    const plannedMinutes = Math.max(0, asNumber(
        status?.planned_minutes !== undefined ? status.planned_minutes : order?.executionTiming?.plannedMinutes,
        0
    ))
    const stageLimitMinutes = Math.max(0, asNumber(
        status?.stage_limit_minutes !== undefined ? status.stage_limit_minutes : order?.executionTiming?.stageLimitMinutes,
        0
    ))
    const createdAt = asNumber(order?.date_create || order?.executionTiming?.orderCreatedAt, 0)
    const executionStatusId = asNumber(order?.executionTiming?.statusId, 0)
    const stageEnteredAt = asNumber(
        executionStatusId === statusId
            ? order?.executionTiming?.statusEnteredAt
            : order?.statusOrder?.time_insert,
        createdAt
    )
    const stageAccumulatedSecondsBeforeCurrent = executionStatusId === statusId
        ? Math.max(0, asNumber(order?.executionTiming?.statusAccumulatedSecondsBeforeCurrent, 0))
        : calculateAccumulatedStatusSeconds(order?.statusOrderAll, statusId, createdAt, stageEnteredAt)
    const now = Math.floor(asNumber(nowMs, Date.now()) / 1000)
    const isTerminal = statusId === 2 || statusId === 3 || asBool(order?.executionTiming?.isTerminal)
    const finalStatus = (statuses || []).find((item) => asNumber(item?.id, 0) === 3) || {}
    const finalPlannedMinutes = Math.max(0, asNumber(
        order?.executionTiming?.finalPlannedMinutes !== undefined
            ? order.executionTiming.finalPlannedMinutes
            : finalStatus?.planned_minutes,
        0
    ))
    const finalStatusName = order?.executionTiming?.finalStatusName || finalStatus?.name || 'Выполнен'
    const finalDeadlineAt = finalPlannedMinutes > 0 && createdAt > 0 ? createdAt + finalPlannedMinutes * 60 : 0
    const finalRemainingSeconds = finalDeadlineAt > 0 ? finalDeadlineAt - now : 0
    const finalOverdue = !isTerminal && finalDeadlineAt > 0 && finalRemainingSeconds < 0
    const historicalViolations = calculateHistoricalViolations(order, statuses, createdAt, now)
    const deadlineAt = plannedMinutes > 0 && createdAt > 0 ? createdAt + plannedMinutes * 60 : 0
    const stageBudgetSeconds = stageLimitMinutes * 60
    const stageDeadlineAt = stageLimitMinutes > 0 && stageEnteredAt > 0
        ? stageEnteredAt + Math.max(0, stageBudgetSeconds - stageAccumulatedSecondsBeforeCurrent)
        : 0
    const remainingSeconds = deadlineAt > 0 ? deadlineAt - now : 0
    const stageCurrentEpisodeSeconds = stageEnteredAt > 0 ? Math.max(0, now - stageEnteredAt) : 0
    const stageTotalElapsedSeconds = stageAccumulatedSecondsBeforeCurrent + stageCurrentEpisodeSeconds
    const stageRemainingSeconds = stageLimitMinutes > 0 ? stageBudgetSeconds - stageTotalElapsedSeconds : 0
    const overdue = !isTerminal && deadlineAt > 0 && remainingSeconds < 0
    const stageOverdue = !isTerminal && stageDeadlineAt > 0 && stageRemainingSeconds < 0

    return {
        configured: plannedMinutes > 0 && createdAt > 0,
        statusId,
        statusName: status?.name || order?.executionTiming?.statusName || '',
        plannedMinutes,
        finalPlannedMinutes,
        finalStatusName,
        stageLimitMinutes,
        createdAt,
        now,
        orderElapsedSeconds: createdAt > 0 ? Math.max(0, now - createdAt) : 0,
        stageEnteredAt,
        stageAccumulatedSecondsBeforeCurrent,
        stageCurrentEpisodeSeconds,
        stageTotalElapsedSeconds,
        stageBudgetSeconds,
        deadlineAt,
        finalDeadlineAt,
        stageDeadlineAt,
        remainingSeconds,
        finalRemainingSeconds,
        stageRemainingSeconds,
        overdue,
        overdueSeconds: overdue ? Math.abs(remainingSeconds) : 0,
        finalOverdue,
        finalOverdueSeconds: finalOverdue ? Math.abs(finalRemainingSeconds) : 0,
        stageOverdue,
        stageOverdueSeconds: stageOverdue ? Math.abs(stageRemainingSeconds) : 0,
        historicalViolations,
        historicalOverdue: historicalViolations.length > 0,
        isTerminal,
    }
}

export const getOrderExecutionBadges = (order, statuses, nowMs = Date.now()) => {
    const timing = calculateOrderExecutionTiming(order, statuses, nowMs)
    if (timing.isTerminal) return []
    const badges = []
    if (timing.overdue || timing.finalOverdue || timing.historicalOverdue) {
        const overdueSeconds = Math.max(
            timing.overdueSeconds,
            timing.finalOverdueSeconds,
            ...timing.historicalViolations.map((item) => asNumber(item?.overdueSeconds, 0))
        )
        badges.push({
            text: `Заказ просрочен на ${formatExecutionDuration(overdueSeconds)}`,
            color: '#dc2626',
            background: '#fff5f5',
            timing,
        })
    } else if ((timing.configured && timing.remainingSeconds <= 15 * 60)
        || (timing.finalPlannedMinutes > 0 && timing.finalRemainingSeconds <= 15 * 60)) {
        const remaining = [
            timing.configured ? timing.remainingSeconds : Number.POSITIVE_INFINITY,
            timing.finalPlannedMinutes > 0 ? timing.finalRemainingSeconds : Number.POSITIVE_INFINITY,
        ]
        badges.push({
            text: `До срока заказа ${formatExecutionDuration(Math.min(...remaining))}`,
            color: '#b45309',
            background: '#fffbeb',
            timing,
        })
    }
    if (timing.stageOverdue) {
        badges.push({
            text: `Этап просрочен на ${formatExecutionDuration(timing.stageOverdueSeconds)}`,
            color: '#b42318',
            background: '#fff7ed',
            timing,
        })
    } else if (timing.stageLimitMinutes > 0 && timing.stageRemainingSeconds <= 15 * 60) {
        badges.push({
            text: `До конца этапа ${formatExecutionDuration(timing.stageRemainingSeconds)}`,
            color: '#9a6700',
            background: '#fffbea',
            timing,
        })
    }
    return badges
}

export const getOrderExecutionBadge = (order, statuses, nowMs = Date.now()) => (
    getOrderExecutionBadges(order, statuses, nowMs)[0] || null
)
